import { useState, useEffect, useCallback, useContext } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Button, 
  Alert, 
  Spinner,
  Card,
  Badge,
  Form,
  Modal,
  Table,
  ButtonGroup
} from 'react-bootstrap';
import { IconEdit } from '@tabler/icons-react';
import Swal from 'sweetalert2';

// Contexts
import NotificationsContext from '../../contexts/NotificationsContext';

// Services
import MarketplaceLeadsServices from './services/MarketplaceLeadsServices';

// Styles
import './marketplace.css';

const MarketplaceLeads = () => {
  const { setNotify } = useContext(NotificationsContext);

  // States
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    person_type: 'all',
    source_page: 'all',
    date_from: '',
    date_to: '',
    page: 1,
    page_size: 20
  });

  // Modal for status update
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [updateData, setUpdateData] = useState({
    status: '',
    notes: ''
  });

  // Load leads
  const loadLeads = useCallback(async (filterParams = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = MarketplaceLeadsServices.buildSearchParams(filterParams);
      const response = await MarketplaceLeadsServices.listLeads(params);
      
      const { results, count } = response.data;
      
      setLeads(results.map(MarketplaceLeadsServices.formatLeadData));
      setPagination({
        currentPage: filterParams.page || 1,
        totalPages: Math.ceil(count / filterParams.page_size),
        totalItems: count,
        itemsPerPage: filterParams.page_size
      });

    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      setError('Erro ao carregar os leads. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await MarketplaceLeadsServices.getLeadsStats();
      const formattedStats = MarketplaceLeadsServices.formatStatsData(response.data);
      setStats(formattedStats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadLeads();
    loadStats();
  }, []);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters, page: 1 };
    setFilters(updatedFilters);
    loadLeads(updatedFilters);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    loadLeads(updatedFilters);
  };

  // Open update modal
  const openUpdateModal = (lead) => {
    setSelectedLead(lead);
    setUpdateData({
      status: lead.status,
      notes: lead.notes || ''
    });
    setShowUpdateModal(true);
  };

  // Close update modal
  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedLead(null);
    setUpdateData({ status: '', notes: '' });
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!selectedLead) return;

    // Validate status transition
    if (updateData.status !== selectedLead.status) {
      const isValidTransition = MarketplaceLeadsServices.validateStatusTransition(
        selectedLead.status, 
        updateData.status
      );
      
      if (!isValidTransition) {
        Swal.fire({
          icon: 'error',
          title: 'Transição Inválida',
          text: 'Esta transição de status não é permitida pelo workflow.',
          confirmButtonColor: '#dc3545'
        });
        return;
      }
    }

    try {
      setActionLoading(selectedLead.id);
      
      await MarketplaceLeadsServices.updateLead(selectedLead.id, {
        status: updateData.status,
        notes: updateData.notes
      });

      setNotify({
        title: "Sucesso!",
        message: "Lead atualizado com sucesso!",
        type: "success",
        visible: true
      });

      closeUpdateModal();
      loadLeads();
      loadStats();

    } catch (error) {
      console.error('Erro ao atualizar lead:', error);
      const errorMessage = MarketplaceLeadsServices.getErrorMessage(error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params = MarketplaceLeadsServices.buildSearchParams(filters);
      delete params.page; // Remove pagination for export
      delete params.page_size;
      
      const response = await MarketplaceLeadsServices.exportLeads(params);
      MarketplaceLeadsServices.downloadCSV(response.data);

      setNotify({
        title: "Sucesso!",
        message: "Exportação concluída!",
        type: "success",
        visible: true
      });

    } catch (error) {
      console.error('Erro ao exportar leads:', error);
      const errorMessage = MarketplaceLeadsServices.getErrorMessage(error);
      Swal.fire({
        icon: 'error',
        title: 'Erro na Exportação',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setExporting(false);
    }
  };

  // Render stats cards
  const renderStatsCards = () => {
    if (!stats) return null;

    return (
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.total_leads}</h3>
              <small className="text-muted">Total de Leads</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{stats.new_leads}</h3>
              <small className="text-muted">Novos</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.qualified_leads}</h3>
              <small className="text-muted">Qualificados</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.converted_leads}</h3>
              <small className="text-muted">Convertidos</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.formattedConversionRate}</h3>
              <small className="text-muted">Conversão</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.leads_today}</h3>
              <small className="text-muted">Hoje</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Render filters
  const renderFilters = () => (
    <Card className="mb-3">
      <Card.Body>
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Buscar</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nome, email ou telefone..."
                value={filters.search}
                onChange={(e) => handleFiltersChange({ search: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFiltersChange({ status: e.target.value })}
              >
                <option value="all">Todos</option>
                <option value="new">Novo</option>
                <option value="contacted">Contatado</option>
                <option value="qualified">Qualificado</option>
                <option value="converted">Convertido</option>
                <option value="lost">Perdido</option>
                <option value="invalid">Inválido</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                value={filters.person_type}
                onChange={(e) => handleFiltersChange({ person_type: e.target.value })}
              >
                <option value="all">Todos</option>
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Data Início</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFiltersChange({ date_from: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Data Fim</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFiltersChange({ date_to: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button
              variant="outline-secondary"
              onClick={() => handleFiltersChange({
                search: '',
                status: 'all',
                person_type: 'all',
                source_page: 'all',
                date_from: '',
                date_to: ''
              })}
            >
              Limpar
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Render leads table
  const renderLeadsTable = () => (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          Leads ({pagination.totalItems})
        </h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Exportando...
            </>
          ) : (
            '📊 Exportar CSV'
          )}
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive hover>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: '200px' }}>Email</th>
              <th style={{ width: '130px', whiteSpace: 'nowrap' }}>Telefone</th>
              <th>Status</th>
              <th>Agente</th>
              <th style={{ whiteSpace: 'nowrap' }}>Data</th>
              <th style={{ width: '60px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <tr key={lead.id}>
                <td>
                  <strong>{lead.name}</strong>
                </td>
                <td style={{ 
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {lead.email}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {lead.phone}
                </td>
               
                <td>
                  <Badge bg={lead.statusVariant} pill>
                    {lead.statusText}
                  </Badge>
                </td>
                <td>
                  {lead.agent ? (
                    <small>
                      <strong>{lead.agent.name}</strong>
                    </small>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
               
                <td style={{ whiteSpace: 'nowrap' }}>
                  <small>{lead.formattedCreatedAt}</small>
                </td>
                <td>
                  {lead.canUpdate && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => openUpdateModal(lead)}
                      disabled={actionLoading === lead.id}
                      style={{ padding: '4px 8px' }}
                    >
                      {actionLoading === lead.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <IconEdit size={16} />
                      )}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );

  // Render pagination
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, pagination.currentPage - 2);
    const endPage = Math.min(pagination.totalPages, pagination.currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === pagination.currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <ButtonGroup>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            Primeira
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          >
            Anterior
          </Button>
          {pages}
          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          >
            Próxima
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => handlePageChange(pagination.totalPages)}
          >
            Última
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  // Render update modal
  const renderUpdateModal = () => (
    <Modal show={showUpdateModal} onHide={closeUpdateModal}>
      <Modal.Header closeButton>
        <Modal.Title>Atualizar Lead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedLead && (
          <>
            <div className="mb-3">
              <strong>Lead:</strong> {selectedLead.name} ({selectedLead.email})
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={updateData.status}
                onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
              >
                <option value={selectedLead.status}>
                  {MarketplaceLeadsServices.getStatusText(selectedLead.status)} (Atual)
                </option>
                {selectedLead.nextStatuses.map(status => (
                  <option key={status} value={status}>
                    {MarketplaceLeadsServices.getStatusText(status)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Observações</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                placeholder="Adicione observações sobre este lead..."
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeUpdateModal}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleStatusUpdate}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Salvando...
            </>
          ) : (
            'Salvar'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (loading) {
    return (
      <Container className="">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="mt-2">Carregando leads...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-2">Leads do Marketplace</h2>
              <p className="text-muted">
                Gerencie os leads interessados nos agentes do marketplace
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {renderStatsCards()}
      {renderFilters()}
      
      {leads.length === 0 && !loading ? (
        <Alert variant="info" className="text-center">
          <h5>Nenhum lead encontrado</h5>
          <p>Ainda não há leads cadastrados com os filtros selecionados.</p>
        </Alert>
      ) : (
        <>
          {renderLeadsTable()}
          {renderPagination()}
        </>
      )}

      {renderUpdateModal()}
    </Container>
  );
};

export default MarketplaceLeads;